import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useKeywordService, useKeywordsService } from '../services';
import { KeywordFormData } from '../types';
import { useCreateKeywordFormDialogController } from './CreateKeywordFormDialogController';

export const useKeywordsViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;

  const {
    loading: keywordsLoading,
    filter: keywordsFilter,
    list: keywordList,
    getKeywords,
    changeKeywordsFilter,
    resetKeywords,
  } = useKeywordsService();
  const { deleteKeyword } = useKeywordService();

  const createKeywordFormDialogController = useCreateKeywordFormDialogController();

  const initLoad = useCallback(async () => {
    try {
      await getKeywords(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadKeywords = useCallback(async () => {
    try {
      await getKeywords(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadKeywordsDebounce = useMemo(() => debounce(reloadKeywords, 500), []);

  const changeKeywordFilterForm = useCallback(async (filter: Partial<RootState["keywords"]["filter"]>) => {
    changeKeywordsFilter(filter);
    await reloadKeywords();
  }, []);

  const changeKeywordsSearchFilter = useCallback(async (_: ChangeEvent<HTMLInputElement>, query: string) => {
    changeKeywordsFilter({ query });
    await reloadKeywordsDebounce();
  }, []);

  const changeKeywordsSorting = useCallback(async (s: GridSortModel) => {
    changeKeywordsFilter({ sort: `${s[0].field}_${s[0].sort}` });
    await reloadKeywords();
  }, []);

  const changeKeywordsPagination = useCallback(async (p: GridPaginationModel) => {
    changeKeywordsFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadKeywords();
  }, []);

  const removeKeyword = useCallback(async (keywordId: string) => {
    try {
      await deleteKeyword(currentCompanyId, keywordId);
      await getKeywords(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    initLoad();

    return () => {
      resetKeywords();
    };
  }, []);

  return {
    isPageLoading: keywordsLoading.getList,
    keywordsFilter,
    keywordList,
    changeKeywordFilterForm,
    changeKeywordsSearchFilter,
    changeKeywordsSorting,
    changeKeywordsPagination,
    removeKeyword,
    createKeywordFormDialogController: {
      ...createKeywordFormDialogController,
      submit: (full: KeywordFormData, dirty: Partial<KeywordFormData>) =>
        createKeywordFormDialogController.submit(full, dirty, () => getKeywords(currentCompanyId)),
    },
  };
};
